import { useMutation } from "@tanstack/react-query";
import {
  Avatar,
  Badge,
  Button,
  FileButton,
  Image,
  Loader,
  Menu,
  Modal,
  Paper,
  Select,
  Switch,
  TextInput,
  Textarea,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import {
  BarChart3,
  Check,
  Mail,
  MessageSquare,
  MoreHorizontal,
  Pencil,
  Trash,
  Upload,
  UserPlus,
  X,
} from "lucide-react";
import React from "react";
import ReactPlayer from "react-player";
import { CSVLink } from "react-csv";
import { Link } from "react-router-dom";
import {
  addCastToMovie,
  addCastToSeries,
  axiosInstance,
  deleteComingSoon,
  deleteMovie,
  deleteSeries,
  toggleEpisodeStatus,
  updateComingSoon,
  updateContentStatus,
  updateMovie,
  updateMovieCast,
  updateSeries,
  updateSeriesCast,
} from "../../utils/api";
import RemoveCast from "../common/RemoveCast";
import DataTable from "../common/DataTable";

const ContentTable = ({
  data,
  loading,
  episodes,
  setUpdate,
  setModal2,
  reload,
  series,
  id,
  coming,
  permissions = {},
  seasons,
  seasonButton,
  onDelete,
  pagination,
  currentPage,
  onViewComments,
  exportCsvData,
  exportCsvFilename,
}) => {
  const [isAddCastModalVisible, setAddCastModalVisible] = React.useState(false);
  const [selectedCastId, setSelectedCastId] = React.useState("");
  const [castData, setCastData] = React.useState({});
  const [isLoading, setIsLoading] = React.useState(false);
  const [isUpdateCastModalVisible, setUpdateCastModalVisible] = React.useState(false);
  const [selectedCastForUpdate, setSelectedCastForUpdate] = React.useState(null);
  const [updateCastData, setUpdateCastData] = React.useState({});
  const [updateCastFile, setUpdateCastFile] = React.useState(null);
  const [castFile, setCastFile] = React.useState(null);
  const [videoModalVisible, setVideoModalVisible] = React.useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = React.useState("");
  const [subtitlesModalVisible, setSubtitlesModalVisible] = React.useState(false);
  const [currentSubtitles, setCurrentSubtitles] = React.useState([]);
  const [creatorModalVisible, setCreatorModalVisible] = React.useState(false);
  const [selectedCreator, setSelectedCreator] = React.useState(null);
  const [statusModalVisible, setStatusModalVisible] = React.useState(false);
  const [selectedContent, setSelectedContent] = React.useState(null);
  const [showMessageField, setShowMessageField] = React.useState(false);
  const [sendMessageModalVisible, setSendMessageModalVisible] = React.useState(false);
  const [selectedContentForMessage, setSelectedContentForMessage] = React.useState(null);
  const [sendEmailModalVisible, setSendEmailModalVisible] = React.useState(false);
  const [selectedContentForEmail, setSelectedContentForEmail] = React.useState(null);

  const statusForm = useForm({ initialValues: { status: "Pending", message: "", isEnable: false } });
  const sendMessageForm = useForm({ initialValues: { message: "" } });
  const sendEmailForm = useForm({ initialValues: { message: "" } });

  const showMsg = (msg, color) => notifications.show({ message: msg, color });

  const toggleStatusMutation = useMutation({
    mutationFn: ({ id, isEnable }) => {
      return coming
        ? updateComingSoon({ id, data: { isEnable } })
        : episodes
        ? toggleEpisodeStatus(id, isEnable)
        : series
        ? updateSeries({ id, data: { isEnable } })
        : updateMovie({ id, data: { isEnable } });
    },
    onSuccess: () => {
      showMsg(
        `${coming ? "Coming Soon" : episodes ? "Episode" : series ? "Series" : "Movie"} status updated successfully`,
        "green"
      );
      reload();
    },
    onError: () => {
      showMsg(`Failed to update ${episodes ? "episode" : series ? "series" : "movie"} status`, "red");
    },
  });

  const toggleFeaturedMutation = useMutation({
    mutationFn: ({ id, isFeatured }) => {
      return coming
        ? updateComingSoon({ id, data: { isFeatured } })
        : updateMovie({ id, data: { isFeatured } });
    },
    onSuccess: () => { showMsg("Featured status updated successfully", "green"); reload(); },
    onError: () => { showMsg("Failed to update featured status", "red"); },
  });

  const updateStatusMutation = useMutation({
    mutationFn: (data) => updateContentStatus(data),
    onSuccess: () => {
      showMsg("Content status updated successfully", "green");
      setStatusModalVisible(false);
      statusForm.reset();
      setSelectedContent(null);
      reload();
    },
    onError: () => { showMsg("Failed to update content status", "red"); },
  });

  const sendMessageMutation = useMutation({
    mutationFn: (data) => axiosInstance.post("/admin/send-message", data),
  });

  const sendEmailMutation = useMutation({
    mutationFn: (data) => axiosInstance.post("/admin/send-email-message", data),
  });

  const updateCastMutation = useMutation({
    mutationFn: ({ data, isSeries }) => {
      return isSeries ? updateSeriesCast(data) : updateMovieCast(data);
    },
    onSuccess: () => {
      showMsg("Cast updated successfully", "green");
      setUpdateCastModalVisible(false);
      setUpdateCastData({});
      setUpdateCastFile(null);
      setSelectedCastForUpdate(null);
      reload();
    },
    onError: () => { showMsg("Failed to update cast", "red"); },
  });

  const handleToggleStatus = (record) => {
    toggleStatusMutation.mutate({
      id: coming ? record.slug : record._id,
      isEnable: !record.isEnable,
    });
  };

  const openVideoModal = (url) => {
    setCurrentVideoUrl(url);
    setVideoModalVisible(true);
  };

  const handleDelete = async (record) => {
    try {
      let success;
      if (episodes && onDelete) {
        success = await onDelete(record._id);
      } else if (series) {
        const [res] = await deleteSeries(record._id);
        success = res;
      } else if (!coming && !seasons) {
        const [res] = await deleteMovie(record._id);
        success = res;
      } else if (coming) {
        const [res] = await deleteComingSoon(record._id);
        success = res;
      }
      if (success) {
        showMsg(`${episodes ? "Episode" : series ? "Series" : "Movie"} deleted successfully`, "green");
        reload();
      }
    } catch (error) {
      showMsg(`Failed to delete ${episodes ? "episode" : series ? "series" : "movie"}`, "red");
    }
  };

  const handleAddCast = async () => {
    if (!castData.name || !castData.role) {
      showMsg("Please fill all cast details", "red");
      return;
    }
    setIsLoading(true);
    try {
      const formData = new FormData();
      if (castFile) formData.append("image", castFile);
      formData.append("name", castData.name);
      formData.append("role", castData.role);
      const [success] = series
        ? await addCastToSeries(selectedCastId, formData)
        : await addCastToMovie(selectedCastId, formData);
      if (success) {
        showMsg("Cast added successfully", "green");
        setAddCastModalVisible(false);
        setCastData({});
        setCastFile(null);
        reload();
      }
    } catch (error) {
      showMsg("Failed to add cast", "red");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateCast = async () => {
    if (!updateCastData.name || !updateCastData.role) {
      showMsg("Please fill all cast details", "red");
      return;
    }
    try {
      const formData = new FormData();
      formData.append("castId", selectedCastForUpdate._id);
      formData.append(series ? "seriesId" : "movieId", selectedCastId);
      formData.append("name", updateCastData.name);
      formData.append("role", updateCastData.role);
      if (updateCastFile) formData.append("image", updateCastFile);
      updateCastMutation.mutate({ data: formData, isSeries: series });
    } catch (error) {
      showMsg("Failed to update cast", "red");
    }
  };

  const openUpdateCastModal = (cast, contentId) => {
    setSelectedCastForUpdate(cast);
    setSelectedCastId(contentId);
    setUpdateCastData({ name: cast.name, role: cast.role });
    setUpdateCastModalVisible(true);
  };

  const getUrlColumns = () => {
    if (coming) return [];
    if (!series && !seasons) {
      return [
        {
          title: "Url", dataIndex: "url", key: "url",
          render: (_, record) => (
            <Button variant="subtle" size="xs" onClick={() => openVideoModal(record.url)}>Play Video</Button>
          ),
        },
        {
          title: "Duration", dataIndex: "duration", key: "duration",
          render: (duration) => {
            if (!duration) return "N/A";
            const hours = Math.floor(duration / 60);
            const minutes = duration % 60;
            return `${hours}h ${minutes}m`;
          },
        },
      ];
    }
    if (series) {
      return [{
        title: "Seasons", dataIndex: "seasons", key: "seasons",
        render: (_, record) => seasonButton && seasonButton(record._id),
      }];
    }
    return [{
      title: "Episodes", dataIndex: "episodes", key: "episodes",
      render: (_, record) => (
        <Link to={`/episodes/${record._id}`}><Button size="xs">Episodes</Button></Link>
      ),
    }];
  };

  const getMetadataColumns = () => {
    const baseColumns = [
      ...(!episodes && !coming ? [
        { title: "Language", dataIndex: "language", key: "language" },
        {
          title: "Genres", dataIndex: "genres", key: "genres",
          render: (genres) => genres?.map((item) => <span key={item.name}>{item.name}, </span>),
        },
        {
          title: "Trailer", dataIndex: "trailer", key: "trailer",
          render: (url) => <Button variant="subtle" size="xs" onClick={() => openVideoModal(url)}>Play Trailer</Button>,
        },
        { title: "Uploaded By", dataIndex: ["uploadedBy", "kind"], key: "uploadedBy" },
        {
          title: "Creator", dataIndex: ["uploadedBy"], key: "person",
          render: (uploadedBy) => (
            <Button
              size="xs"
              onClick={() => { setSelectedCreator(uploadedBy?.person); setCreatorModalVisible(true); }}
              disabled={!uploadedBy?.person || uploadedBy?.kind === "admins"}
            >
              {uploadedBy?.person ? uploadedBy?.person?.email : "No Creator"}
            </Button>
          ),
        },
      ] : []),
      ...(!coming ? [{
        title: "Subtitles", dataIndex: "subtitles", key: "subtitles",
        render: (subtitles) =>
          subtitles?.length > 0 ? (
            <Button variant="subtle" size="xs" onClick={() => { setCurrentSubtitles(subtitles); setSubtitlesModalVisible(true); }}>
              View Subtitles ({subtitles.length})
            </Button>
          ) : "No subtitles",
      }] : []),
    ];

    if (!coming && !episodes) {
      baseColumns.push({
        title: "Cast", dataIndex: "cast", key: "cast",
        render: (cast, record) => (
          <div>
            {cast?.map((item) => (
              <div key={item._id} style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                <span>{item.name} ({item.role})</span>
                {(permissions?.updateSeries || permissions?.updateMovie) && (
                  <Button variant="subtle" size="xs" p={0} onClick={() => openUpdateCastModal(item, record._id)}>
                    <Pencil size={12} />
                  </Button>
                )}
              </div>
            ))}
          </div>
        ),
      });
    }
    return baseColumns;
  };

  const getDeleteButton = (record) => {
    return coming ? permissions?.deleteComingSoon : series ? permissions?.deleteSeries : episodes ? permissions?.deleteSeries : permissions?.deleteMovie;
  };

  const getEditButton = (record) => {
    if (episodes) return true;
    return coming ? permissions?.updateComingSoon : series ? permissions?.updateSeries : permissions?.updateMovie;
  };

  const getActionColumn = () => ({
    title: "Action", key: "action",
    render: (_, record) => (
      <Menu shadow="md" width={200} position="bottom-end">
        <Menu.Target>
          <Button variant="default" size="xs"><MoreHorizontal size={16} /></Button>
        </Menu.Target>
        <Menu.Dropdown>
          {getEditButton(record) && (
            <Menu.Item>
              {episodes ? (
                <button onClick={() => { setModal2(true); setUpdate(record); }} className="flex items-center gap-2 w-full">
                  <Pencil size={16} /> Edit
                </button>
              ) : (
                <Link to={`/${series ? "series" : coming ? "coming-soon" : "movies"}/edit/${record._id}?page=${currentPage}`} className="flex items-center gap-2">
                  <Pencil size={16} /> Edit
                </Link>
              )}
            </Menu.Item>
          )}
          {!coming && !series && (
            <Menu.Item>
              <Link to={`/analytics/${episodes ? "episode" : "movie"}/${record._id}`} className="flex items-center gap-2">
                <BarChart3 size={16} /> Analytics
              </Link>
            </Menu.Item>
          )}
          {getDeleteButton(record) && (
            <Menu.Item color="red" onClick={() => {
              modals.openConfirmModal({
                title: "Are you sure you want to delete this item?",
                labels: { confirm: "Delete", cancel: "Cancel" },
                confirmProps: { color: "red" },
                onConfirm: () => handleDelete(record),
              });
            }}>
              <span className="flex items-center gap-2"><Trash size={16} /> Delete</span>
            </Menu.Item>
          )}
          {onViewComments && (
            <Menu.Item onClick={() => onViewComments(record)}>
              <span className="flex items-center gap-2"><MessageSquare size={16} /> Comments</span>
            </Menu.Item>
          )}
          {(permissions?.updateSeries || permissions?.updateMovie) && !episodes && !coming && (
            <>
              <Menu.Item onClick={() => { setAddCastModalVisible(true); setSelectedCastId(record._id); setCastData({}); setCastFile(null); }}>
                <span className="flex items-center gap-2"><UserPlus size={16} /> Add Cast</span>
              </Menu.Item>
              <Menu.Item>
                <RemoveCast id={record._id} type={series ? "series" : "movie"} reload={reload} record={record} />
              </Menu.Item>
              <Menu.Item>
                <RemoveFromSubscription type={series ? "series" : "movie"} currentItem={record?.subscriptions} reload={reload} id={record._id} />
              </Menu.Item>
            </>
          )}
          {!coming && !episodes && (
            <>
              <Menu.Item onClick={() => { setSelectedContentForMessage(record); setSendMessageModalVisible(true); sendMessageForm.reset(); }}>
                <span className="flex items-center gap-2"><MessageSquare size={16} /> Send Message</span>
              </Menu.Item>
              <Menu.Item onClick={() => { setSelectedContentForEmail(record); setSendEmailModalVisible(true); sendEmailForm.reset(); }}>
                <span className="flex items-center gap-2"><Mail size={16} /> Send Email</span>
              </Menu.Item>
            </>
          )}
        </Menu.Dropdown>
      </Menu>
    ),
  });

  const columns = [
    getActionColumn(),
    { title: "Name", dataIndex: "name", key: "name" },
    {
      title: "Status", dataIndex: "isEnable", key: "isEnable",
      render: (isEnable) => (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 20, height: 20, borderRadius: "50%", border: `1px solid ${isEnable ? "#00ff0050" : "#ff000050"}`, backgroundColor: isEnable ? "#00ff0050" : "#ff000050" }}>
          {isEnable ? <Check size={16} /> : <X size={16} />}
        </div>
      ),
    },
    {
      title: "Status Action", key: "statusAction",
      render: (_, record) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {!coming && !episodes && (
            <Button size="xs" fullWidth onClick={() => {
              setSelectedContent(record);
              setStatusModalVisible(true);
              statusForm.setValues({ status: record.status || "Pending", message: "", isEnable: record.isEnable });
            }} disabled={coming ? !permissions?.updateComingSoon : !permissions?.isPublishAccess}>
              Update Status
            </Button>
          )}
          <Button size="xs" color={record.isEnable ? "red" : undefined} fullWidth
            onClick={() => handleToggleStatus(record)} loading={toggleStatusMutation.isPending}
            disabled={coming ? !permissions?.updateComingSoon : episodes ? !permissions?.updateSeries : !permissions?.isPublishAccess}>
            {!record.isEnable ? "Enable" : "Disable"}
          </Button>
          {!series && !coming && !episodes && (permissions?.updateSeries || permissions?.updateMovie) && (
            <Button size="xs" variant={record.isFeatured ? "default" : "filled"} fullWidth
              onClick={() => toggleFeaturedMutation.mutate({ id: record._id, isFeatured: !record.isFeatured })}
              loading={toggleFeaturedMutation.isPending}>
              {record.isFeatured ? "Remove from Featured" : "Add to Featured"}
            </Button>
          )}
        </div>
      ),
    },
    {
      title: "Description", dataIndex: "description", key: "description",
      render: (text) => <div style={{ width: 300, overflow: "hidden" }}>{text?.slice(0, 100)}</div>,
    },
    ...(!coming && !episodes ? [{
      title: "Release Date", dataIndex: "releaseDate", key: "releaseDate",
      render: (date) => {
        if (!date) return "N/A";
        return new Date(date).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
      },
    }] : []),
    ...getUrlColumns(),
    ...getMetadataColumns(),
    {
      title: "Horizontal Banner", dataIndex: "horizontalBanner", key: "horizontalBanner",
      render: (url) => <Image src={url} w={100} h={100} alt="horizontal banner" />,
    },
    {
      title: "Vertical Banner", dataIndex: "verticalBanner", key: "verticalBanner",
      render: (url) => <Image src={url} w={100} h={100} alt="vertical banner" />,
    },
    !coming && !episodes && {
      title: "Strip Enable", dataIndex: "stripEnable", key: "stripEnable",
      render: (stripEnable) => (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 20, height: 20, borderRadius: "50%", border: `1px solid ${stripEnable ? "#00ff0050" : "#ff000050"}`, backgroundColor: stripEnable ? "#00ff0050" : "#ff000050" }}>
          {stripEnable ? <Check size={16} /> : <X size={16} />}
        </div>
      ),
    },
    !coming && !episodes && {
      title: "Strip Text", dataIndex: "stripText", key: "stripText",
      render: (stripText, record) => {
        if (!record.stripEnable) return "N/A";
        return <div style={{ width: 200, overflow: "hidden" }}>{stripText ? stripText.slice(0, 50) + (stripText.length > 50 ? "..." : "") : "No text"}</div>;
      },
    },
    coming && { title: "Release Date", dataIndex: "releaseDate", key: "releaseDate",
      render: (date) => { if (!date) return "N/A"; return new Date(date).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }); }
    },
    coming && { title: "Genres", dataIndex: "genres", key: "genres",
      render: (genres) => genres?.map((item) => <span key={item.name || item}>{item.name || item}, </span>),
    },
    coming && { title: "Age Limit", dataIndex: "ageLimit", key: "ageLimit" },
  ].filter(Boolean);

  const csvData = (exportCsvData || data || []).map((row) => {
    const csvRow = {
      name: row.name || "", description: row.description || "", isEnable: row.isEnable ? "Yes" : "No",
      position: row.position || "", horizontalBanner: row.horizontalBanner || "", verticalBanner: row.verticalBanner || "",
      duration: row.duration || "", subtitles: row.subtitles?.map((s) => `${s.language}: ${s.link}`).join("; ") || "",
    };
    if (!episodes) {
      csvRow.status = row.status || ""; csvRow.releaseDate = row.releaseDate ? new Date(row.releaseDate).toLocaleDateString() : "";
      csvRow.language = row.language || ""; csvRow.genres = row.genres?.map((g) => g.name || g).join(", ") || "";
      csvRow.ageLimit = row.ageLimit || ""; csvRow.stripEnable = row.stripEnable ? "Yes" : "No";
      csvRow.stripText = row.stripText || ""; csvRow.isFeatured = row.isFeatured ? "Yes" : "No";
      csvRow.view = row.view || ""; csvRow.type = row.type || ""; csvRow.videoId = row.videoId || "";
      csvRow.cast = row.cast?.map((c) => `${c.name} (${c.role})`).join("; ") || "";
      csvRow.uploadedBy = row.uploadedBy?.kind || ""; csvRow.creator = row.uploadedBy?.person?.email || "";
    }
    return csvRow;
  });

  return (
    <>
      {exportCsvData && exportCsvFilename && (
        <div className="flex justify-end mb-4">
          <CSVLink data={csvData} filename={exportCsvFilename} target="_blank">
            <Button>Export as CSV</Button>
          </CSVLink>
        </div>
      )}

      <DataTable
        rowKey={(record) => record._id}
        scroll={{ x: 1000 }}
        dataSource={episodes ? data : data?.slice()?.reverse()}
        loading={loading}
        columns={columns}
        pagination={pagination}
      />

      {/* Add Cast Modal */}
      <Modal opened={isAddCastModalVisible} onClose={() => { setAddCastModalVisible(false); setCastData({}); setCastFile(null); }} title="Add Cast">
        <div className="flex flex-col gap-3">
          <TextInput label="Name" placeholder="Enter cast name" value={castData.name || ""} onChange={(e) => setCastData((p) => ({ ...p, name: e.target.value }))} required />
          <TextInput label="Role" placeholder="Enter cast role" value={castData.role || ""} onChange={(e) => setCastData((p) => ({ ...p, role: e.target.value }))} required />
          <div>
            <FileButton onChange={(file) => { setCastFile(file); setCastData((p) => ({ ...p, image: file })); }} accept="image/*">
              {(props) => <Button variant="default" leftSection={<Upload size={16} />} {...props}>Upload Image</Button>}
            </FileButton>
            {castFile && <p className="text-xs text-gray-400 mt-1">{castFile.name}</p>}
          </div>
          <Button onClick={handleAddCast} loading={isLoading}>Add Cast</Button>
        </div>
      </Modal>

      {/* Update Cast Modal */}
      <Modal opened={isUpdateCastModalVisible} onClose={() => { setUpdateCastModalVisible(false); setUpdateCastData({}); setUpdateCastFile(null); setSelectedCastForUpdate(null); }} title="Update Cast">
        <div className="flex flex-col gap-3">
          <TextInput label="Name" placeholder="Enter cast name" value={updateCastData.name || ""} onChange={(e) => setUpdateCastData((p) => ({ ...p, name: e.target.value }))} required />
          <TextInput label="Role" placeholder="Enter cast role" value={updateCastData.role || ""} onChange={(e) => setUpdateCastData((p) => ({ ...p, role: e.target.value }))} required />
          <div>
            <FileButton onChange={(file) => { setUpdateCastFile(file); setUpdateCastData((p) => ({ ...p, image: file })); }} accept="image/*">
              {(props) => <Button variant="default" leftSection={<Upload size={16} />} {...props}>Upload New Image</Button>}
            </FileButton>
            {updateCastFile && <p className="text-xs text-gray-400 mt-1">{updateCastFile.name}</p>}
          </div>
          <Button onClick={handleUpdateCast} loading={updateCastMutation.isPending}>Update Cast</Button>
        </div>
      </Modal>

      {/* Video Modal */}
      <Modal opened={videoModalVisible} onClose={() => { setVideoModalVisible(false); setCurrentVideoUrl(""); }} size="lg" withCloseButton>
        <div style={{ width: "100%", height: "450px" }}>
          <ReactPlayer url={currentVideoUrl} width="100%" height="100%" controls playing
            config={{ file: { forceHLS: true, hlsOptions: { xhrSetup: (xhr) => { xhr.setRequestHeader("Referer", "https://admin.vtv.co.tz/"); xhr.setRequestHeader("Origin", "https://admin.vtv.co.tz"); } } } }} />
        </div>
      </Modal>

      {/* Subtitles Modal */}
      <Modal opened={subtitlesModalVisible} onClose={() => { setSubtitlesModalVisible(false); setCurrentSubtitles([]); }} title="Available Subtitles">
        <div style={{ maxHeight: "400px", overflowY: "auto" }}>
          {currentSubtitles?.map((subtitle, index) => (
            <div key={index} style={{ padding: "12px", borderBottom: "1px solid #333", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontWeight: "bold" }}>{subtitle.language}</div>
                <div style={{ color: "#666", fontSize: "12px" }}>{subtitle.link}</div>
              </div>
              <Button variant="subtle" component="a" href={subtitle.link} target="_blank" rel="noopener noreferrer" size="xs">Download</Button>
            </div>
          ))}
        </div>
      </Modal>

      {/* Creator Modal */}
      <Modal opened={creatorModalVisible} onClose={() => { setCreatorModalVisible(false); setSelectedCreator(null); }} title="Creator Details" size="sm">
        {selectedCreator && (
          <div>
            <Paper withBorder p="md" mb="md">
              <div style={{ display: "flex", alignItems: "center" }}>
                <Avatar color="blue" mr="md">{selectedCreator.name.charAt(0).toUpperCase()}</Avatar>
                <div>
                  <div style={{ fontSize: "16px", fontWeight: "bold" }}>{selectedCreator.name}</div>
                  <Badge color={selectedCreator.isVerified ? "green" : "red"}>{selectedCreator.isVerified ? "Verified" : "Not Verified"}</Badge>
                </div>
              </div>
            </Paper>
            <Paper withBorder p="md">
              <div style={{ marginBottom: "12px" }}>
                <div style={{ color: "#8c8c8c", fontSize: "12px", marginBottom: "4px" }}>Email</div>
                <div style={{ fontSize: "14px" }}>{selectedCreator.email}</div>
              </div>
              <div>
                <div style={{ color: "#8c8c8c", fontSize: "12px", marginBottom: "4px" }}>Phone</div>
                <div style={{ fontSize: "14px" }}>{selectedCreator.phoneNumber}</div>
              </div>
            </Paper>
          </div>
        )}
      </Modal>

      {/* Status Modal */}
      <Modal opened={statusModalVisible} onClose={() => { setStatusModalVisible(false); setSelectedContent(null); statusForm.reset(); setShowMessageField(false); }} title="Update Content Status">
        <form onSubmit={statusForm.onSubmit((values) => {
          updateStatusMutation.mutate({
            status: values.status, message: values.status === "Rejected" ? values.message : "",
            type: coming ? "ComingSoon" : series ? "Series" : "Movie", id: selectedContent?._id, isEnable: values.isEnable,
          });
        })}>
          <Select label="Status" data={[{ value: "Approved", label: "Approved" }, { value: "Rejected", label: "Rejected" }, { value: "Pending", label: "Pending" }]}
            mb="sm" {...statusForm.getInputProps("status")}
            onChange={(val) => { statusForm.setFieldValue("status", val); setShowMessageField(val === "Rejected"); }} />
          {showMessageField && <Textarea label="Rejection Reason" rows={4} mb="sm" {...statusForm.getInputProps("message")} />}
          <Switch label="Enable/Disable" mb="sm" checked={statusForm.values.isEnable} onChange={(e) => statusForm.setFieldValue("isEnable", e.currentTarget.checked)} />
          <Button type="submit" loading={updateStatusMutation.isPending}>Update Status</Button>
        </form>
      </Modal>

      {/* Send Message Modal */}
      <Modal opened={sendMessageModalVisible} onClose={() => { setSendMessageModalVisible(false); setSelectedContentForMessage(null); sendMessageForm.reset(); }} title="Send Message">
        <form onSubmit={sendMessageForm.onSubmit((values) => {
          sendMessageMutation.mutate({ message: values.message });
          showMsg("Message sent successfully", "green");
          setSendMessageModalVisible(false); sendMessageForm.reset(); setSelectedContentForMessage(null);
        })}>
          <Textarea label="Message" rows={4} placeholder="Enter your message here..." mb="sm" {...sendMessageForm.getInputProps("message")} required />
          <Button type="submit">Send Message</Button>
        </form>
      </Modal>

      {/* Send Email Modal */}
      <Modal opened={sendEmailModalVisible} onClose={() => { setSendEmailModalVisible(false); setSelectedContentForEmail(null); sendEmailForm.reset(); }} title="Send Email">
        <form onSubmit={sendEmailForm.onSubmit((values) => {
          sendEmailMutation.mutate({ message: values.message });
          showMsg("Email sent successfully", "green");
          setSendEmailModalVisible(false); sendEmailForm.reset(); setSelectedContentForEmail(null);
        })}>
          <Textarea label="Email Message" rows={4} placeholder="Enter your email message here..." mb="sm" {...sendEmailForm.getInputProps("message")} required />
          <Button type="submit">Send Email</Button>
        </form>
      </Modal>
    </>
  );
};

export default ContentTable;

export const RemoveFromSubscription = ({ type, reload, currentItem, id }) => {
  const [open, setOpen] = React.useState(false);
  const [selectedSub, setSelectedSub] = React.useState(null);

  const removeFromSubscriptionMutation = useMutation({
    mutationFn: (data) => axiosInstance.put(`/admin/subscription/remove-video`, data),
    onSuccess: () => { notifications.show({ message: "Item removed from subscription", color: "green" }); reload(); setOpen(false); },
    onError: () => notifications.show({ message: "Failed to remove item from subscription", color: "red" }),
  });

  return (
    <>
      <button onClick={() => setOpen(true)} className="flex items-center gap-2">
        <Trash size={16} /> Remove from Subscription
      </button>
      <Modal opened={open} onClose={() => setOpen(false)} title="Remove from Subscription">
        <Select
          placeholder="Select subscription"
          data={(currentItem || []).map((item) => ({ label: item?.title, value: item?._id }))}
          value={selectedSub}
          onChange={setSelectedSub}
          mb="md"
        />
        <Button onClick={() => {
          if (!selectedSub) return;
          removeFromSubscriptionMutation.mutate(
            type === "movie"
              ? { subscriptionId: selectedSub, movieId: id, type: "Movie" }
              : { subscriptionId: selectedSub, seriesId: id, type: "Series" }
          );
        }}>
          Remove
        </Button>
      </Modal>
    </>
  );
};
